/**
 * ModeAccordionItem — single row of the editor's mode accordion.
 *
 * Header: numbered chip (1–4 in mono — phosphor when open, panel-lo when
 * closed) + icon + title + status text + chevron. Body: rendered when
 * `open`. Pattern lifted from
 * `design_handoff_tank/tank-redesign/screens-editor.jsx § Accordion`.
 *
 * The `<ModeAccordion>` parent injects `open` and `onToggle` via
 * cloneElement; consumers don't pass them directly.
 */

import { ChevronDownIcon } from '@heroicons/react/24/outline';

import styles from './ModeAccordion.module.css';

/**
 * @param {Object} props
 * @param {string} props.itemKey   — stable key matched against the parent's `active`
 * @param {string} props.n         — number badge (e.g. "1")
 * @param {string} props.title     — section title (e.g. "Crop")
 * @param {React.ReactNode} [props.icon]   — section icon
 * @param {React.ReactNode} [props.summary] — collapsed-state status text (mono small)
 * @param {boolean} [props.open]   — injected by `<ModeAccordion>`; do not pass
 * @param {() => void} [props.onToggle] — injected by `<ModeAccordion>`; do not pass
 * @param {React.ReactNode} [props.children]
 */
export default function ModeAccordionItem({
  itemKey, // eslint-disable-line no-unused-vars -- consumed by parent via clone
  n,
  title,
  icon,
  summary,
  open = false,
  onToggle,
  children,
}) {
  return (
    <section className={`${styles.item} ${open ? styles.itemOpen : ''}`}>
      <button
        type="button"
        className={styles.header}
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className={`${styles.numChip} ${open ? styles.numChipOpen : ''}`}>{n}</span>
        {icon && <span className={`${styles.icon} ${open ? styles.iconOpen : ''}`}>{icon}</span>}
        <span className={styles.title}>{title}</span>
        {summary && <span className={styles.summary}>{summary}</span>}
        <ChevronDownIcon
          className={`${styles.chev} ${open ? styles.chevOpen : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && children && <div className={styles.body}>{children}</div>}
    </section>
  );
}
